<template>
  <div class="dashboard-page">
    <Panel size="small" hide-header>
      <template v-slot:content>
        <div class="d-flex justify-content-end mb-3">
          <VDatepicker
            v-model="dateRange"
            range
            placeholder="Select date range"
            :clearable="true"
            :shortcuts="shortcuts"
            value-type="date"
            format="MMM DD, YYYY"
            @input="onDateChange"
          />
        </div>

        <div v-if="loading" class="text-center text-primary my-5">
          <b-spinner class="align-middle"></b-spinner>
          <strong> Loading...</strong>
        </div>

        <div v-else-if="!formattedHistory.length" class="text-center my-4 text-muted">
          No transaction history available.
        </div>

        <div v-else class="asymmetric-timeline px-md-3 py-2">
          <div class="spine-line"></div>
          
          <div 
            v-for="(item, index) in formattedHistory" 
            :key="index"
            class="timeline-row"
            :class="item.isPositive ? 'row-positive' : 'row-negative'"
          >
            <!-- Marker (Always positioned on the spine) -->
            <div class="marker-node" :class="item.typeClass">
              <font-awesome-icon :icon="item.icon" size="sm" />
            </div>

            <!-- Content Area -->
            <div class="content-side">
              <div class="content-wrapper">
                <!-- Amount -->
                <div class="data-amount" :class="item.typeClass">
                  {{ item.sign }}{{ formatCurrency(item.diffAbs) }}
                </div>

                <!-- Date (Highlighted - Same Color) -->
                <div class="data-date mt-1" :class="item.typeClass">
                  {{ dateFormat(item.date, item.dateDisplayFormat) }}
                  <span class="ml-1 opacity-75">{{ dateFormat(item.date, 'h:mm A') }}</span>
                </div>

                <!-- Type (Small) -->
                <div class="data-type text-muted mt-1">
                  {{ item.displayType }}
                  <span v-if="item.changedBy">
                    <span class="mx-1">&bull;</span> {{ item.changedBy }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Panel>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import formatCurrency from '@/utils/formatCurrency'
import dateFormatMixin from '@/mixins/dateFormatMixin'

export default {
  name: 'BalanceHistory',
  mixins: [dateFormatMixin],
  data() {
    return {
      loading: false,
      dateRange: [],
      shortcuts: [
        {
          text: 'Today',
          onClick() {
            const end = new Date()
            const start = new Date()
            return [start, end]
          }
        },
        {
          text: 'Yesterday',
          onClick() {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24)
            end.setTime(end.getTime() - 3600 * 1000 * 24)
            return [start, end]
          }
        },
        {
          text: 'Last 7 Days',
          onClick() {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
            return [start, end]
          }
        },
        {
          text: 'This Month',
          onClick() {
            const end = new Date()
            const start = new Date()
            start.setDate(1)
            return [start, end]
          }
        },
        {
          text: 'Last Month',
          onClick() {
            const start = new Date()
            start.setMonth(start.getMonth() - 1)
            start.setDate(1)
            
            const end = new Date()
            end.setDate(0) // Last day of previous month
            
            return [start, end]
          }
        },
        {
          text: 'This Year',
          onClick() {
            const end = new Date()
            const start = new Date(new Date().getFullYear(), 0)
            return [start, end]
          }
        },
        {
          text: 'Last 6 Months',
          onClick() {
            const end = new Date()
            const start = new Date()
            start.setMonth(start.getMonth() - 6)
            return [start, end]
          }
        },
        {
          text: 'Last Year',
          onClick() {
            const end = new Date()
            const start = new Date()
            start.setFullYear(start.getFullYear() - 1)
            return [start, end]
          }
        },
        {
          text: 'All Time',
          onClick() {
            const end = new Date()
            const start = new Date(2015, 0, 1)
            return [start, end]
          }
        }
      ]
    }
  },

  computed: {
    ...mapState('billing', ['balanceHistory']),
    
    formattedHistory() {
      if (!Array.isArray(this.balanceHistory)) return []
      
      const sorted = [...this.balanceHistory].sort((a, b) => new Date(b.date) - new Date(a.date))

      return sorted.map(item => {
        const diff = item.amount !== undefined ? item.amount : (item.newBalance - item.previousBalance)
        const isPositive = diff >= 0
        
        let displayType = item.type || item.description || (isPositive ? 'Funds Added' : 'Funds Deducted')
        
        const descLower = (item.description || '').toLowerCase()
        const typeLower = (item.type || '').toLowerCase()
        
        // Default Settings
        let typeClass = 'text-danger'
        let icon = ['fas', 'file-invoice-dollar']

        if (isPositive) {
          typeClass = 'text-success'
          icon = ['fas', 'wallet']
        }

        // Color & Icon Heuristics
        if (descLower.includes('credit card') || typeLower.includes('call')) {
          typeClass = 'text-blue'
          icon = ['fas', 'credit-card']
        } else if (descLower.includes('wire') || typeLower.includes('wire')) {
          typeClass = 'text-teal'
          icon = ['fas', 'university']
        } else if (descLower.includes('auto') || typeLower.includes('auto')) {
          typeClass = 'text-info'
          icon = ['fas', 'sync-alt']
        } else if (descLower.includes('admin') || typeLower.includes('admin') || typeLower.includes('adjustment')) {
          if (isPositive) {
            typeClass = 'text-blue' // Admin (Positive) -> Blue
            icon = ['fas', 'user-cog']
          } else {
            typeClass = 'text-purple' // Admin (Negative) -> Purple
            icon = ['fas', 'user-cog']
          }
        } else if (descLower.includes('refund') || typeLower.includes('refund')) {
          typeClass = 'text-warning'
          icon = ['fas', 'undo'] 
        } else if (!isPositive) {
          typeClass = 'text-purple' // Negative -> Purple (previously Admin color)
          icon = ['fas', 'file-invoice-dollar']
        }

        const dateRaw = item.date || item.changedDate
        const dateObj = new Date(dateRaw)
        const currentYear = new Date().getFullYear()
        const isCurrentYear = dateObj.getFullYear() === currentYear
        const dateDisplayFormat = isCurrentYear ? 'MMM DD' : 'MMM DD, YYYY'

        return {
          ...item,
          diff,
          diffAbs: Math.abs(diff || 0),
          sign: isPositive ? '+' : '-',
          typeClass,
          isPositive,
          icon,
          displayType,
          date: dateRaw,
          dateDisplayFormat
        }
      })
    }
  },

  async created() {
    this.loading = true
    try {
      await this.fetchBalanceHistory()
    } finally {
      this.loading = false
    }
  },

  methods: {
    ...mapActions('billing', ['fetchBalanceHistory']),
    formatCurrency,

    async onDateChange() {
      this.loading = true
      try {
        const [startDate, endDate] = this.dateRange || []
        await this.fetchBalanceHistory({ 
          startDate: startDate ? startDate.toISOString() : null, 
          endDate: endDate ? endDate.toISOString() : null 
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
$spine-offset: 250px;
// Below md (see PHONE): the spine 11px in, so its 24px markers start at the timeline's left edge,
// and the text 16px after them.
$phone-spine-offset: 11px;
$phone-content-offset: 40px;

.asymmetric-timeline {
  position: relative;
  width: 100%;
  padding-top: 20px;
}

.spine-line {
  position: absolute;
  left: $spine-offset;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 2px dashed rgba(255, 255, 255, 0.15);
  z-index: 0;
}

.timeline-row {
  position: relative;
  margin-bottom: 30px;
  min-height: 50px;
}

.marker-node {
  position: absolute;
  left: $spine-offset;
  top: 10px; /* Push down to align with amount text approx center */
  width: 24px;
  height: 24px;
  margin-left: -13px; /* Exact center: width/2 + border width (2px/2=1) = 13? No. 
                         Width 24/2 = 12. Line is 2px width centered on spine-offset.
                         If spine-offset is left: 250px.
                         Left: 250px places left edge at 250px.
                         We want center at 250px + 1px (line center).
                         So Left = 251px - 12px = 239px?
                         Actually, spine-line has width 0 and border-left 2px.
                         So the line is at x=250px to x=252px. Center is 251px.
                         Marker width 24px. Center is 12px.
                         Start should be 251 - 12 = 239px.
                         margin-left: -12px relative to 251px?
                         Let's just use left: $spine-offset and transform */
  transform: translateX(1px) translateX(-50%); /* 1px shifts to center of 2px border, -50% centers the dot */
  
  border-radius: 50%;
  background: #1e2634;
  border: 2px solid #2c3e50;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem; /* Smaller icon */
  box-shadow: 0 0 0 6px #1e2634; /* Large gap effect */
  z-index: 2;

  &.text-success { border-color: #00ff61; color: #00ff61; box-shadow: 0 0 10px rgba(0, 255, 97, 0.2), 0 0 0 6px #1e2634; }
  &.text-danger  { border-color: #ff4757; color: #ff4757; box-shadow: 0 0 10px rgba(255, 71, 87, 0.2), 0 0 0 6px #1e2634; }
  &.text-info    { border-color: #00ceff; color: #00ceff; box-shadow: 0 0 10px rgba(0, 206, 255, 0.2), 0 0 0 6px #1e2634; }
  &.text-purple  { border-color: #9b59b6; color: #cc99ff; box-shadow: 0 0 10px rgba(155, 89, 182, 0.2), 0 0 0 6px #1e2634; }
  &.text-warning { border-color: #fb6340; color: #ffcb8c; box-shadow: 0 0 10px rgba(251, 99, 64, 0.2), 0 0 0 6px #1e2634; }
  &.text-blue    { border-color: #2d98da; color: #45aaf2; box-shadow: 0 0 10px rgba(45, 152, 218, 0.2), 0 0 0 6px #1e2634; }
  &.text-teal    { border-color: #20bf6b; color: #26de81; box-shadow: 0 0 10px rgba(32, 191, 107, 0.2), 0 0 0 6px #1e2634; }
}

/* 
  NEGATIVE (Left Side)
  Constrained width to fit left of spine
*/
.row-negative {
  .content-side {
    width: $spine-offset;
    text-align: right;
    padding-right: 45px; /* Clear marker + gap */
    display: flex;
    justify-content: flex-end;
  }
}

/* 
  POSITIVE (Right Side)
  Margin left to start after spine
*/
.row-positive {
  .content-side {
    margin-left: $spine-offset;
    text-align: left;
    padding-left: 45px; /* Clear marker + gap */
    display: flex;
    justify-content: flex-start;
  }
}

.content-wrapper {
  display: flex;
  flex-direction: column;
}

/*
  PHONE (below md)
  No room for two sides: at 360px the timeline is about 260px wide, so a spine 250px in left every
  credit's text past the panel's edge, and the page scrolled sideways. The spine moves to the left,
  with the markers level with the date picker above, and every entry is on its right. The sign and
  the colour still tell a credit from a debit.
*/
@include media-breakpoint-down(sm) {
  .spine-line,
  .marker-node {
    left: $phone-spine-offset;
  }

  // The transform alone centres it on the spine here. The desktop's -13px margin offsets most of the
  // timeline's padding, which the markers sit inside and the spine does not. A phone has no padding,
  // so the margin would put the markers 13px left of the spine.
  .marker-node {
    margin-left: 0;
  }

  .row-negative .content-side,
  .row-positive .content-side {
    width: auto;
    margin-left: 0;
    text-align: left;
    padding-left: $phone-content-offset;
    padding-right: 0;
    justify-content: flex-start;
  }
}

/* Typography */
.data-amount {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
}

.data-date {
  font-size: 0.9rem;
  font-weight: 500;
}

.text-light-muted {
  color: #a0aec0;
  font-weight: 400;
  font-size: 0.8rem;
  margin-left: 4px;
}

.data-type {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #8898aa;
}
.text-blue { color: #45aaf2; }
.text-teal { color: #26de81; }
.text-purple { color: #cc99ff; }
// The timeline's own colours for four of Bootstrap's text utilities, which are !important themselves.
// stylelint-disable declaration-no-important -- over Bootstrap's !important .text-warning, -info, -success and -danger
.text-warning { color: #ffcb8c !important; }
.text-info { color: #00ceff !important; }
.text-success { color: #00ff61 !important; }
.text-danger { color: #ff4757 !important; }
// stylelint-enable declaration-no-important
</style>
